import { FC, useState } from 'react';
import Modal from '@new/common/ui/Modal/Modal';
import { Button } from '@new/common/ui';
import {
  useBookSlotMutation,
  type UserAvailabilitySlot,
  type ScheduledCall,
} from '@teachur/http-client';
import { CalendarWithSlots } from '@new/common/ui/TailwindCalendar/components/CalendarWithSlots/CalendarWithSlots';
import { TimezoneDisplay } from './TimezoneDisplay';
import { ScheduleSuccessModal } from './ScheduleSuccessModal';

interface StudentScheduleProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: number;
  onScheduleSuccess?: () => void;
}

export const StudentSchedule: FC<StudentScheduleProps> = ({
  isOpen,
  onClose,
  courseId,
  onScheduleSuccess,
}) => {
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [scheduledCall, setScheduledCall] = useState<ScheduledCall | undefined>(undefined);

  const bookSlotMutation = useBookSlotMutation(courseId);

  const handleSlotClick = (slot: UserAvailabilitySlot) => {
    bookSlotMutation.mutate(slot.id, {
      onSuccess: (data) => {
        setScheduledCall(data);
        setIsSuccessModalOpen(true);
        onScheduleSuccess?.();
      },
    });
  };

  const handleSchedule = () => {
    if (selectedSlotId) handleSlotClick({ id: selectedSlotId } as UserAvailabilitySlot);
  };

  const handleSuccessClose = () => {
    setIsSuccessModalOpen(false);
    setScheduledCall(undefined);
    setSelectedSlotId(null);
    onClose();
  };

  if (isSuccessModalOpen && scheduledCall) {
    return (
      <ScheduleSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={handleSuccessClose}
        scheduledCall={scheduledCall}
      />
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Select date and time" className="lg:max-w-4xl">
      <div className="flex h-full min-h-0 flex-col gap-6">
        <div className="flex flex-1 min-h-0 flex-col gap-6 lg:gap-8">
          <div className="flex flex-1 min-h-0 flex-col gap-4 overflow-y-auto">
            <CalendarWithSlots
              courseId={courseId}
              onBookedSlot={onClose}
              selectedSlotId={selectedSlotId}
              onSlotSelect={setSelectedSlotId}
            />
            <TimezoneDisplay />
          </div>

          <div className="flex gap-4">
            <Button
              variant="outline"
              size="lg"
              onClick={onClose}
              className="flex-1 text-(--primary-O900)!"
            >
              Cancel
            </Button>

            <Button
              variant="primary"
              size="lg"
              onClick={handleSchedule}
              disabled={!selectedSlotId || bookSlotMutation.isPending}
              className="flex-1"
            >
              Schedule
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
